import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add'; // Importar o ícone Add corretamente
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { Lead } from '../types/lead';
import { EmailMonitoringContent } from './EmailMonitoringPage'; // Importar apenas o conteúdo da página de monitoramento
import EmailAIContent from '../components/EmailAIContent';
import EmailAutoResponder from '../components/EmailAutoResponder';

// Interface para e-mail
interface Email {
  id: string;
  subject: string;
  body: string;
  to: string;
  from: string;
  date: Date;
  read: boolean;
  replied: boolean;
  leadId?: string;
  trackingId?: string;
  readTimestamp?: Date;
}

// Interface para modelo de resposta
interface ResponseTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
}

// Interface para evento de leitura de e-mail
interface EmailReadEvent {
  trackingId: string;
  email: string;
  timestamp: Date;
  userAgent?: string;
  ipAddress?: string;
}

const EmailResponsePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [emails, setEmails] = useState<Email[]>([]);
  const [templates, setTemplates] = useState<ResponseTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responseSubject, setResponseSubject] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [newTemplate, setNewTemplate] = useState<Omit<ResponseTemplate, 'id'>>({
    name: '',
    subject: '',
    body: '',
    variables: []
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [enableTracking, setEnableTracking] = useState(true);
  const [readEvents, setReadEvents] = useState<EmailReadEvent[]>([]);
  const [openTrackingDialog, setOpenTrackingDialog] = useState(false);
  const [selectedTrackingId, setSelectedTrackingId] = useState<string | null>(null);

  const leads = useSelector((state: RootState) => state.leads.items);

  // Simular carregamento de e-mails
  useEffect(() => {
    const loadEmails = async () => {
      setLoading(true);
      try {
        // Aqui seria uma chamada à API para carregar e-mails
        // Simulando dados para demonstração
        const mockEmails: Email[] = [
          {
            id: '1',
            subject: 'Interesse em imóvel no centro',
            body: 'Olá, gostaria de mais informações sobre o apartamento no centro que vi no site. Qual o valor do condomínio? Tem vaga de garagem? Obrigado, João Silva (joao.silva@email.com)',
            to: 'contato@imobiliaria.com',
            from: 'joao.silva@email.com',
            date: new Date(2025, 3, 5, 15, 30),
            read: true,
            replied: false,
            leadId: leads.length > 0 ? leads[0].id : undefined,
            trackingId: 'trk_1617642000000_abc123',
            readTimestamp: new Date(2025, 3, 5, 16, 45)
          },
          {
            id: '2',
            subject: 'Dúvida sobre financiamento',
            body: 'Boa tarde, tenho interesse em um dos imóveis do seu site e gostaria de saber se vocês trabalham com financiamento pela Caixa. Aguardo retorno. Maria Oliveira (maria.oliveira@email.com)',
            to: 'contato@imobiliaria.com',
            from: 'maria.oliveira@email.com',
            date: new Date(2025, 3, 5, 10, 15),
            read: false,
            replied: false,
            trackingId: 'trk_1617638400000_def456'
          },
          {
            id: '3',
            subject: 'Agendamento de visita',
            body: 'Prezados, gostaria de agendar uma visita para conhecer o imóvel da Rua das Flores, 123. Estou disponível nesta quinta ou sexta-feira à tarde. Atenciosamente, Carlos Mendes (carlos.mendes@email.com)',
            to: 'contato@imobiliaria.com',
            from: 'carlos.mendes@email.com',
            date: new Date(2025, 3, 4, 17, 45),
            read: true,
            replied: true,
            trackingId: 'trk_1617556800000_ghi789',
            readTimestamp: new Date(2025, 3, 4, 18, 30)
          }
        ];
        
        setEmails(mockEmails);
        
        // Carregar templates
        const mockTemplates: ResponseTemplate[] = [
          {
            id: '1',
            name: 'Resposta Padrão',
            subject: 'RE: {{assunto_original}}',
            body: 'Olá {{nome}},\n\nObrigado por entrar em contato conosco.\n\n{{resposta_personalizada}}\n\nEstamos à disposição para qualquer dúvida adicional.\n\nAtenciosamente,\nEquipe de Vendas',
            variables: ['nome', 'assunto_original', 'resposta_personalizada']
          },
          {
            id: '2',
            name: 'Agendamento de Visita',
            subject: 'Agendamento de Visita - {{endereco_imovel}}',
            body: 'Olá {{nome}},\n\nRecebemos sua solicitação de visita ao imóvel {{endereco_imovel}}.\n\nTemos disponibilidade para atendê-lo(a) {{data_horario}}. Por favor, confirme se essa data é conveniente para você.\n\nAtenciosamente,\nEquipe de Vendas',
            variables: ['nome', 'endereco_imovel', 'data_horario']
          },
          {
            id: '3',
            name: 'Informações de Financiamento',
            subject: 'Informações sobre Financiamento',
            body: 'Olá {{nome}},\n\nObrigado pelo seu interesse em nossos imóveis.\n\nSim, trabalhamos com financiamento pela Caixa e também com outros bancos. Para avançarmos com o processo, precisaríamos das seguintes informações:\n\n- Comprovante de renda\n- Extrato bancário dos últimos 3 meses\n- Documento de identidade e CPF\n\nPodemos agendar uma reunião para discutir as melhores opções para o seu caso?\n\nAtenciosamente,\nEquipe de Vendas',
            variables: ['nome']
          }
        ];
        
        setTemplates(mockTemplates);

        // Carregar eventos de leitura (simulação)
        const mockReadEvents: EmailReadEvent[] = [
          {
            trackingId: 'trk_1617642000000_abc123',
            email: 'joao.silva@email.com',
            timestamp: new Date(2025, 3, 5, 16, 45),
            userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            ipAddress: '187.122.45.67'
          },
          {
            trackingId: 'trk_1617556800000_ghi789',
            email: 'carlos.mendes@email.com',
            timestamp: new Date(2025, 3, 4, 18, 30),
            userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_4 like Mac OS X)',
            ipAddress: '200.158.12.34'
          }
        ];
        
        setReadEvents(mockReadEvents);
      } catch (error) {
        console.error('Erro ao carregar e-mails:', error);
        setSnackbarMessage('Erro ao carregar e-mails. Tente novamente.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    
    loadEmails();
  }, [leads]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEmailSelect = (email: Email) => {
    setSelectedEmail(email);
    setResponseSubject(`RE: ${email.subject}`);
    
    // Extrair possível nome do remetente
    const nameMatch = email.body.match(/([A-Z][a-z]+ [A-Z][a-z]+)/);
    const name = nameMatch ? nameMatch[1].split(' ')[0] : '';
    
    // Preparar resposta básica
    setResponseText(`Olá ${name},\n\nObrigado por entrar em contato conosco.\n\n\n\nAtenciosamente,\nEquipe de Vendas`);
  };

  const handleTemplateChange = (event: SelectChangeEvent) => {
    const templateId = event.target.value;
    setSelectedTemplate(templateId);
    
    if (templateId && selectedEmail) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        // Extrair possível nome do remetente
        const nameMatch = selectedEmail.body.match(/([A-Z][a-z]+ [A-Z][a-z]+)/);
        const name = nameMatch ? nameMatch[1].split(' ')[0] : '';
        
        // Substituir variáveis básicas
        let subject = template.subject.replace('{{assunto_original}}', selectedEmail.subject);
        let body = template.body.replace('{{nome}}', name);
        
        setResponseSubject(subject);
        setResponseText(body);
      }
    }
  };

  const handleSendEmail = () => {
    if (!selectedEmail || !responseText) return;
    
    setLoading(true);
    
    // Simulação de envio de resposta
    setTimeout(() => {
      // Gerar ID de rastreamento se o rastreamento estiver habilitado
      const trackingId = enableTracking ? `trk_${Date.now()}_${Math.random().toString(36).substring(2, 15)}` : undefined;
      
      // Atualizar o e-mail como respondido
      setEmails(prevEmails => 
        prevEmails.map(email => 
          email.id === selectedEmail.id 
            ? { ...email, replied: true } 
            : email
        )
      );
      
      setSelectedEmail(null);
      setResponseText('');
      setResponseSubject('');
      setSelectedTemplate('');
      
      setSnackbarMessage(`Resposta enviada com sucesso! ${enableTracking ? 'Rastreamento de leitura ativado.' : ''}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      setLoading(false);
    }, 1500);
  };

  const handleSaveTemplate = () => {
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
      setSnackbarMessage('Preencha todos os campos do template');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Extrair variáveis do template (texto entre {{ }})
    const variableRegex = /{{([^}]+)}}/g;
    const matches = newTemplate.body.match(variableRegex) || [];
    const variables = matches.map(match => match.replace(/{{|}}/g, ''));
    
    const newTemplateWithId: ResponseTemplate = {
      ...newTemplate,
      id: `template_${Date.now()}`,
      variables: Array.from(new Set(variables)) // Converter Set para Array
    };
    
    setTemplates(prev => [...prev, newTemplateWithId]);
    setOpenTemplateDialog(false);
    setNewTemplate({
      name: '',
      subject: '',
      body: '',
      variables: []
    });
    
    setSnackbarMessage('Template salvo com sucesso!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const extractEmailFromText = (text: string): string | null => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const matches = text.match(emailRegex);
    return matches ? matches[0] : null;
  };

  const findOrCreateLead = (email: Email) => {
    // Extrair e-mail do texto
    const emailAddress = extractEmailFromText(email.body) || email.from;
    
    // Extrair possível nome
    const nameMatch = email.body.match(/([A-Z][a-z]+ [A-Z][a-z]+)/);
    const name = nameMatch ? nameMatch[1] : 'Contato via E-mail';
    
    setSnackbarMessage(`Lead criado/atualizado para: ${name} (${emailAddress})`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleViewTrackingEvents = (trackingId: string) => {
    setSelectedTrackingId(trackingId);
    setOpenTrackingDialog(true);
  };

  const getTrackingEvents = (trackingId: string) => {
    return readEvents.filter(event => event.trackingId === trackingId);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        E-mail Response
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Caixa de Entrada" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="Templates" icon={<SaveIcon />} iconPosition="start" />
          <Tab label="Configurações" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="Monitoramento de E-mails" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Assistente IA" icon={<AutoAwesomeIcon />} iconPosition="start" />
          <Tab label="Automação" icon={<ScheduleIcon />} iconPosition="start" />
        </Tabs>
      </Box>
      
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '70vh', overflow: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">E-mails</Typography>
                <IconButton onClick={() => setEmails(emails)}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {emails.map(email => (
                    <React.Fragment key={email.id}>
                      <ListItem 
                        button 
                        onClick={() => handleEmailSelect(email)}
                        selected={selectedEmail?.id === email.id}
                        sx={{ 
                          bgcolor: email.read ? 'transparent' : 'action.hover',
                          borderLeft: email.read ? 'none' : '3px solid',
                          borderColor: 'primary.main'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: email.replied ? 'success.main' : 'primary.main' }}>
                            {email.from.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body1" noWrap sx={{ maxWidth: '70%' }}>
                                {email.subject}
                              </Typography>
                              <Typography variant="caption">
                                {new Date(email.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" noWrap>
                                {email.from}
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {email.body.substring(0, 60)}...
                              </Typography>
                              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {email.replied && (
                                  <Chip 
                                    label="Respondido" 
                                    size="small" 
                                    color="success" 
                                  />
                                )}
                                {email.readTimestamp && (
                                  <Tooltip title={`Lido em ${new Date(email.readTimestamp).toLocaleString()}`}>
                                    <Chip 
                                      icon={<VisibilityIcon fontSize="small" />}
                                      label="Lido" 
                                      size="small" 
                                      color="info"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (email.trackingId) {
                                          handleViewTrackingEvents(email.trackingId);
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={8}>
            {selectedEmail ? (
              <Paper sx={{ p: 2, height: '70vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {selectedEmail.subject}
                  </Typography>
                  <Box>
                    <Button 
                      startIcon={<AddIcon />}
                      variant="outlined" 
                      size="small" 
                      onClick={() => findOrCreateLead(selectedEmail)}
                      sx={{ mr: 1 }}
                    >
                      Criar/Atualizar Lead
                    </Button>
                    {selectedEmail.trackingId && (
                      <Tooltip title="Ver eventos de rastreamento">
                        <IconButton 
                          color="primary"
                          onClick={() => handleViewTrackingEvents(selectedEmail.trackingId!)}
                        >
                          <TimelineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', flex: '0 0 auto' }}>
                  <Typography variant="subtitle2">
                    De: {selectedEmail.from}
                  </Typography>
                  <Typography variant="subtitle2">
                    Para: {selectedEmail.to}
                  </Typography>
                  <Typography variant="subtitle2">
                    Data: {new Date(selectedEmail.date).toLocaleString()}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {selectedEmail.body}
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Template de Resposta</InputLabel>
                    <Select
                      value={selectedTemplate}
                      label="Template de Resposta"
                      onChange={handleTemplateChange}
                    >
                      <MenuItem value="">
                        <em>Nenhum</em>
                      </MenuItem>
                      {templates.map(template => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Assunto"
                    fullWidth
                    value={responseSubject}
                    onChange={(e) => setResponseSubject(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    label="Resposta"
                    multiline
                    rows={10}
                    fullWidth
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    sx={{ mb: 2, flex: 1 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableTracking}
                          onChange={(e) => setEnableTracking(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {enableTracking ? <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} /> : <VisibilityOffIcon fontSize="small" sx={{ mr: 0.5 }} />}
                          <Typography variant="body2">Rastreamento de leitura</Typography>
                        </Box>
                      }
                    />
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<SendIcon />}
                      onClick={handleSendEmail}
                      disabled={loading || !responseText}
                    >
                      Enviar Resposta
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <EmailIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Selecione um e-mail para responder
                  </Typography>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}
      
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Templates de Resposta</Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setOpenTemplateDialog(true)}
              >
                Novo Template
              </Button>
            </Box>
          </Grid>
          
          {templates.map(template => (
            <Grid item xs={12} md={4} key={template.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Assunto: {template.subject}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ mb: 2, height: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {template.body}
                  </Typography>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Variáveis:
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {template.variables.map(variable => (
                        <Chip 
                          key={variable} 
                          label={variable} 
                          size="small" 
                          sx={{ mr: 0.5, mb: 0.5 }} 
                        />
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <IconButton color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Configurações de E-mail
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Configure as integrações de e-mail para automatizar respostas e captura de leads.
              </Alert>
              
              <Typography variant="subtitle1" gutterBottom>
                Conta de E-mail
              </Typography>
              <TextField
                label="E-mail"
                fullWidth
                defaultValue="contato@imobiliaria.com"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Nome de Exibição"
                fullWidth
                defaultValue="Equipe de Vendas"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Configurações de Captura de Leads
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Captura Automática de Leads</InputLabel>
                <Select
                  defaultValue="enabled"
                  label="Captura Automática de Leads"
                >
                  <MenuItem value="enabled">Habilitada</MenuItem>
                  <MenuItem value="disabled">Desabilitada</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Configurações de Rastreamento
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableTracking}
                    onChange={(e) => setEnableTracking(e.target.checked)}
                    color="primary"
                  />
                }
                label="Habilitar rastreamento de leitura por padrão"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                Quando habilitado, um pixel de rastreamento invisível (1x1px) será adicionado aos e-mails enviados para detectar quando são abertos.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={<SaveIcon />}
                >
                  Salvar Configurações
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EmailMonitoringContent />
          </Grid>
        </Grid>
      )}
      
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EmailAIContent onSendEmail={handleSendEmail} />
          </Grid>
        </Grid>
      )}
      
      {activeTab === 5 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EmailAutoResponder />
          </Grid>
        </Grid>
      )}
      
      {/* Dialog para criar novo template */}
      <Dialog open={openTemplateDialog} onClose={() => setOpenTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Novo Template de Resposta</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome do Template"
            fullWidth
            value={newTemplate.name}
            onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="Assunto"
            fullWidth
            value={newTemplate.subject}
            onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
            sx={{ mb: 2 }}
            helperText="Use {{assunto_original}} para incluir o assunto original"
          />
          <TextField
            label="Corpo da Mensagem"
            multiline
            rows={10}
            fullWidth
            value={newTemplate.body}
            onChange={(e) => setNewTemplate({...newTemplate, body: e.target.value})}
            sx={{ mb: 2 }}
            helperText="Use {{nome}} para incluir o nome do contato e outras variáveis entre chaves duplas"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTemplateDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveTemplate} variant="contained">Salvar Template</Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para visualizar eventos de rastreamento */}
      <Dialog open={openTrackingDialog} onClose={() => setOpenTrackingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimelineIcon sx={{ mr: 1 }} />
            Eventos de Rastreamento
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedTrackingId && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                ID de Rastreamento: {selectedTrackingId}
              </Typography>
              <Divider sx={{ my: 2 }} />
              
              {getTrackingEvents(selectedTrackingId).length > 0 ? (
                <List>
                  {getTrackingEvents(selectedTrackingId).map((event, index) => (
                    <ListItem key={index} divider={index < getTrackingEvents(selectedTrackingId).length - 1}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <VisibilityIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`E-mail lido por ${event.email}`}
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              Data: {new Date(event.timestamp).toLocaleString()}
                            </Typography>
                            <br />
                            {event.userAgent && (
                              <Typography variant="body2" component="span">
                                Dispositivo: {event.userAgent.substring(0, 50)}...
                              </Typography>
                            )}
                            {event.ipAddress && (
                              <>
                                <br />
                                <Typography variant="body2" component="span">
                                  IP: {event.ipAddress}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  Nenhum evento de rastreamento encontrado para este ID.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTrackingDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmailResponsePage;
